/**
 * Utility per la geocodifica inversa - ottenere la città dalle coordinate
 */

export interface GeocodeResult {
  city?: string;
  country?: string;
  error?: string;
}

/**
 * Ottiene la città dalle coordinate usando l'API di geocodifica inversa
 * @param latitude Latitudine
 * @param longitude Longitudine
 * @returns Promise con il risultato della geocodifica
 */
export async function getCityFromCoordinates(
  latitude: number, 
  longitude: number
): Promise<GeocodeResult> {
  try {
    // Utilizziamo l'API gratuita di OpenStreetMap Nominatim
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Gildy-App/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data && data.address) {
      const address = data.address;
      
      // Proviamo a ottenere la città da diversi campi possibili
      const city = address.city || 
                   address.town || 
                   address.village || 
                   address.municipality || 
                   address.county ||
                   address.state_district ||
                   address.state;
      
      const country = address.country;
      
      return {
        city: city || undefined,
        country: country || undefined
      };
    }
    
    return { error: 'Nessun risultato trovato per le coordinate fornite' };
  } catch (error) {
    console.error('Errore nella geocodifica inversa:', error);
    return { 
      error: error instanceof Error ? error.message : 'Errore sconosciuto nella geocodifica'
    };
  }
}

/**
 * Ottiene la città dalle coordinate con cache locale per evitare richieste multiple
 */
const geocodeCache = new Map<string, GeocodeResult>();

export async function getCityFromCoordinatesWithCache(
  latitude: number, 
  longitude: number
): Promise<GeocodeResult> {
  // Creiamo una chiave cache arrotondata per evitare troppe richieste per coordinate simili
  const roundedLat = Math.round(latitude * 100) / 100;
  const roundedLon = Math.round(longitude * 100) / 100;
  const cacheKey = `${roundedLat},${roundedLon}`;
  
  // Controlliamo se abbiamo già il risultato in cache
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }
  
  // Facciamo la richiesta e salviamo in cache
  const result = await getCityFromCoordinates(latitude, longitude);
  geocodeCache.set(cacheKey, result);
  
  return result;
}
